import { FC, FormEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";
import { FormLayout } from "@/components/Forms";
import useOnCloseWithConfirmation from "@/hooks/useOnCloseWithConfirmation";
import FormField from "@/components/Forms/FormField";
import { IgProfile } from "@/types/resources";
import { IgProfileTier } from "@/utils/enums";

interface FormDataT {
  username: string;
  tier: IgProfileTier | null;
}

interface Props {
  onClose: () => void;
  editableProfile?: IgProfile;
}

const Form: FC<Props> = ({ onClose, editableProfile }) => {
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    clearErrors,
    setError,
    control,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      username: editableProfile?.username ?? "",
      tier: editableProfile?.tier ?? null,
    },
  });

  const alerts = useAlerts();

  const closeDialog = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        if (editableProfile) {
          await api.put(
            `/v1/ig-profiles-tracker/profiles/${editableProfile.id}`,
            data
          );
        } else {
          await api.post(`/v1/ig-profiles-tracker/profiles`, data);
        }

        await queryClient.invalidateQueries(["ig_trackable_profiles"]);

        alerts.success(
          `Profile was successfully ${
            !!editableProfile ? "updated" : "created"
          }`
        );

        onClose();
      } catch (err) {
        handleFormErrors(err, setError, alerts);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel
        heading={`${editableProfile ? "Edit" : "New"} instagram profile`}
        onClose={closeDialog}
      >
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="username"
                label="Username"
                disabled={!!editableProfile}
              />
              <FormField<FormDataT, number>
                type="select"
                control={control}
                name="tier"
                label="Tier"
                options={[
                  {
                    label: "S (20min)",
                    value: IgProfileTier.S,
                  },
                  {
                    label: "A (1h)",
                    value: IgProfileTier.A,
                  },
                  {
                    label: "B (3h)",
                    value: IgProfileTier.B,
                  },
                  {
                    label: "C (12h)",
                    value: IgProfileTier.C,
                  },
                  {
                    label: "D (24h)",
                    value: IgProfileTier.D,
                  },
                ]}
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              {editableProfile ? "Update" : "Add"}
            </Button>
            <Button color="secondary" onClick={closeDialog} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default Form;
